import { Button, Divider, Grid, Group, Image, Modal, Select, Table, Text, TextInput, useMantineTheme } from '@mantine/core';
import { IconArrowLeft, IconArrowRight, IconEye, IconEyeOff, IconFilter } from '@tabler/icons-react';
import { highlightFilterValue, stringFilterFn } from 'mantine-data-grid';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { capitalizeFirstLetter } from '../../Utils/utils';
import NavBar from '../../components/NavBar/NavBar';
import PoReport from '../../components/Reports/PoReport';
import { URL_PREFIX } from '../../constants/stores';
import { PoNoWithStatus } from '../../interface/AllocationInvoice';
import { formatStatus } from '../Floor User/Forklift';
import '../Global.css';
import classes from './PoWorksheet.module.css';

const PoWorksheet: React.FC = () => {
    const location = useLocation();
    const [rowData, setRowData] = useState<PoNoWithStatus>();
    const token = useSelector((state: any) => state.user.sessionId);
    const [tableData, setTableData] = useState<any[]>([]);
    const [filterModal, setFilterModal] = useState<boolean>(false);
    const [filter, setFilter] = useState<{ style_no: string, po_no: string, supplier_name: string, description: string }>({
        style_no: '',
        po_no: '',
        supplier_name: '',
        description: ''
    });
    const [pagination, setPagination] = useState<{ page: number, limit: number, total?: number }>({ page: 1, limit: 10, total: 1 });
    const [viewModal, setViewModal] = useState<boolean>(false);
    const [selectedStyle, setSelectedStyle] = useState<{ image: string, style_no: string }>({ image: '', style_no: '' });
    const theme = useMantineTheme();

    useEffect(() => {
        const row = location.state as PoNoWithStatus;
        setRowData(row);
        setFilter({ ...filter, po_no: filter.po_no && filter.po_no !== '' ? filter.po_no : row.poNo || '' })
        getData(filter.po_no && filter.po_no !== '' ? filter.po_no : row.poNo || '', pagination.page, pagination.limit);
    }, [pagination.limit, pagination.page]);
    const [expandedRows, setExpandedRow] = useState<string[]>([]);
    const columns = useMemo(() => [
        {
            accessorKey: 'image',
            header: 'Img',
            filterFn: stringFilterFn,
            cell: highlightFilterValue,
            width: 50,
        },
        {
            accessorKey: 'style_no',
            header: 'Style No',
            filterFn: stringFilterFn,
            cell: highlightFilterValue,
        },
        {
            accessorKey: 'description',
            header: 'Description',
            filterFn: stringFilterFn,
        },
        {
            accessorKey: 'color',
            header: 'Color',
            filterFn: stringFilterFn,
        },
        {
            accessorKey: 'cost',
            header: 'Cost',
            size: 70,
        },
        {
            accessorKey: 'msrp',
            header: 'MSRP',
            size: 90,
        },
        {
            accessorKey: 'supplierName',
            header: 'Supplier',
            filterFn: stringFilterFn,
            size: 120,
        },
        {
            accessorKey: 'po_no',
            header: 'PoNo',
            filterFn: stringFilterFn,
        },
        {
            accessorKey: 'order_status',
            header: 'Status',
        },
        {
            accessorKey: 'total',
            header: 'Total',
            size: 70,
        },
        {
            accessorKey: 'total_discrepancy',
            header: 'Total Discrepancy',
            filterFn: stringFilterFn,
            cell: ({ getValue }) => {
                const value = getValue() as number | string;
                return (
                    <div style={{
                        color: value !== null ? theme.colors.gray[8] : undefined,
                        width: '60px',
                        height: '20px',
                        textAlign: 'center',
                        backgroundColor: value !== null ? theme.colors.gray[1] : undefined,
                        fontWeight: value !== null ? 600 : undefined,
                    }}>
                        {value}
                    </div>
                );
            },
        },
        {
            accessorKey: 'driver',
            header: 'Driver',
            filterFn: stringFilterFn,
        },
        {
            accessorKey: 'notes',
            header: 'Notes',
            filterFn: stringFilterFn,
        },
    ], []);

    const getData = async (po_no: string, page: number, limit: number) => {
        try {
            const response = await fetch('/allocation/transferList', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    po_id: rowData?.poId,
                    ...filter,
                    po_no: po_no ? po_no : filter.po_no,
                    like: false,
                    page,
                    limit
                })
            });
            const { data, total } = await response.json();
            if (response.status === 200) {
                setTableData(data.map((item: any, index: number) => {
                    let totalRcvAlloc = item.receivedQty.reduce((a: number, b: { size: string, quantity: number }) => a + b.quantity, 0);
                    let disc = item.receivedQty.map((element: { size: string, quantity: number }, index: number) => {
                        return (element?.quantity - item.style_receivedQty[index]?.quantity) || 0;
                    });
                    let total_discrepancy = disc.every((value: number) => value === 0) ? null : disc.reduce((a: number, b: number) => a + b, 0);
                    return {
                        style_no: item.style_no,
                        description: item.description,
                        image: item.image,
                        color: item.color,
                        cost: item.cost,
                        msrp: item.msrp,
                        supplierName: item.supplierName,
                        po_no: item.po_no,
                        total: totalRcvAlloc,
                        total_discrepancy: total_discrepancy,
                        notes: item.notes,
                        driver: item.driver,
                        temp: item.style_receivedQty,
                        order_status: formatStatus(item.order_status),
                        totalAllocationPerSize: item.totalAllocationPerSize,
                        overstockPerSize: item.overstockPerSize,
                        existingStyle: item.existingStyle,
                        expanded: item.receivedQty.map((element: { size: string, quantity: number }, index: number) => {
                            return {
                                size: element.size,
                                rcvQty: element?.quantity,
                                alloc: item.totalAllocationPerSize[index],
                                overstock: item.overstockPerSize[index],
                                pickedQty: item.pickedQty[index]?.quantity || 0,
                                discrepancy: disc[index]
                            }
                        })
                    }
                }));
                setPagination(prev => ({ ...prev, total: total }));
                return;
            } else {
                alert('Could not find the data. Please check the filter values again.');
                setTableData([]);
                setPagination(prev => ({ ...prev, page: 1, total: 1 }));
                return;
            }
        } catch (error) {
            console.log('Error fetcing Po workesheet data.', error)
        }
    }

    const handlePagination = (operation: string) => {
        if (operation === 'increase') {
            setPagination({ ...pagination, page: pagination.page === pagination.total ? pagination.page : pagination.page + 1 })
        } else {
            setPagination({ ...pagination, page: pagination.page === 1 ? pagination.page : pagination.page - 1 })
        }
    }

    // Function to render the expanded row details
    const renderExpandedRowDetails = (row: any) => {
        return (
            <Table withColumnBorders withTableBorder striped stickyHeader style={{ width: '96vw', borderCollapse: 'collapse' }}>
                <Table.Thead>
                    <Table.Tr style={{ backgroundColor: theme.colors.blue[6] }}>
                        <Table.Th>Size</Table.Th>
                        {row.expanded.map((item: any) => {
                            return <Table.Th>{item.size}</Table.Th>
                        })}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    <Table.Tr>
                        <Table.Th>RCV QTY</Table.Th>
                        {row.expanded.map((item: any) => {
                            return <Table.Td>{item.rcvQty}</Table.Td>
                        })}
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Total Allocation</Table.Th>
                        {row.expanded.map((item: any) => {
                            return <Table.Td>{item.alloc}</Table.Td>
                        })}
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Picked Qty</Table.Th>
                        {row.expanded.map((item: any) => {
                            const difference = Number(item.pickedQty || 0) - Number(item.alloc || 0);
                            return <Table.Td style={{ color: difference === 0 ? '#000000' : difference > 0 ? '#1B5E20' : '#B71C1C', fontWeight: difference !== 0 ? 700 : undefined }}>{item.pickedQty}</Table.Td>
                        })}
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Overstock</Table.Th>
                        {row.expanded.map((item: any) => {
                            return <Table.Td>{item.overstock}</Table.Td>
                        })}
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Discrepancy</Table.Th>
                        {row.expanded.map((item: any) => {
                            return <Table.Td style={{
                                backgroundColor: item.discrepancy > 0 ? '#1B5E20' : item.discrepancy < 0 ? '#B71C1C' : undefined,
                                color: item.discrepancy !== 0 ? theme.colors.gray[0] : undefined,
                                fontWeight: item.discrepancy !== 0 ? 600 : undefined,
                            }}>{item.discrepancy}</Table.Td>
                        })}
                    </Table.Tr>
                </Table.Tbody>
            </Table>
        );
    };
    const handleOpenModal = async (image: any, style_no: string) => {
        if (image?.startsWith('data:image/')) {
            await setSelectedStyle({ image, style_no })
        } else {
            await setSelectedStyle({ image: image, style_no })
        }
        setViewModal(true);
    }
    const ViewImageModal = () => {
        return (
            <Modal
                opened={viewModal}
                onClose={() => setViewModal(false)}
                size={'xl'}
            >
                <div className={classes.viewModal}>
                    <Text fw={600} fz={20} style={{ textAlign: 'center' }}>{selectedStyle?.style_no}</Text>
                    <Divider my={'sm'} />
                    <Image
                        src={`${URL_PREFIX}/uploads/${selectedStyle?.image}`}
                        alt={`${selectedStyle?.style_no}`}
                    />
                </div>
            </Modal>

        )
    }

    const handleApplyFilter = async () => {
        await getData(filter.po_no, pagination.page, pagination.limit);
        setFilterModal(false);
        setPagination({ ...pagination, page: 1 });
    }

    return (
        <div>
            <ViewImageModal />
            <NavBar />
            <div className={classes.main}>
                <h2>PO Worksheet</h2>
                <div style={{ width: '98vw', overflow: 'hidden', marginBottom: '50px' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                        <TextInput
                            type='text'
                            placeholder='Search style no . . . '
                            value={filter.style_no}
                            className={classes.infoText}
                            style={{ marginBottom: '10px', flexGrow: 1 }}
                            onChange={(e) => setFilter({ ...filter, style_no: e.target.value.toUpperCase() })}
                        />
                        <Button style={{ backgroundColor: theme.colors.gray[8], width: '10vw' }} onClick={handleApplyFilter}>Search</Button>
                    </div>
                    {tableData?.length > 0 && (<Table withColumnBorders withRowBorders withTableBorder verticalSpacing={'sm'}>
                        <Table.Thead>
                            <Table.Tr>
                                {columns.map((column: any) => {
                                    return (
                                        <Table.Th className={classes.tableHead} style={{ width: column.width || undefined }} key={column.header}>{column.header}</Table.Th>
                                    )
                                })}
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {tableData?.length > 0 && tableData.map((row: any, index: number) => {
                                return (
                                    <>
                                        <Table.Tr key={`${index}-${row.style_no}`} onClick={() => setExpandedRow((expandedRows.includes(row.style_no) ? expandedRows.filter((item: any) => item !== row.style_no) : [...expandedRows, row.style_no]))} style={{ cursor: 'pointer' }}>
                                            {columns.map((column: any, indx: number) => {
                                                if (column.accessorKey === 'description') {
                                                    return (<Table.Td className={classes.tableData}>{capitalizeFirstLetter(row[column.accessorKey])}</Table.Td>)
                                                }
                                                if (column.accessorKey === 'image') {
                                                    return (<Table.Td style={{ cursor: 'pointer', backgroundColor: '#e6e6f0' }} className={classes.tableData} onClick={() => {
                                                        handleOpenModal(row.image, row.style_no);

                                                    }}>{row[column.accessorKey] ? <IconEye /> : <IconEyeOff />}</Table.Td>)
                                                }
                                                return (<Table.Td className={classes.tableData}>{row[column.accessorKey]}</Table.Td>)
                                            })}
                                        </Table.Tr>
                                        {expandedRows.includes(row.style_no) && <Table.Tr>
                                            <Table.Td colSpan={columns.length} >
                                                {renderExpandedRowDetails(row)}
                                            </Table.Td>
                                        </Table.Tr>}
                                    </>
                                )
                            })}
                        </Table.Tbody>
                    </Table>)}
                </div>
            </div>
            <div className={"footerDiv"}>
                <div className={classes.paginationGroup}>
                    <Button variant='outline' className={classes.bottomBtn} onClick={() => setFilterModal(true)} ><IconFilter /> Filter</Button>
                </div>
                <Group>
                    <div className={classes.paginationGroup}>
                        <Button disabled={pagination.page === 1} className={classes.pagincationIcon} size='xs' onClick={() => handlePagination('decrease')}><IconArrowLeft /></Button>
                        <Text size='lg' fw={500}>{pagination.page}/{pagination.total || 1}</Text>
                        <Button disabled={pagination.page === pagination.total} className={classes.pagincationIcon} size='xs' onClick={() => handlePagination('increase')}><IconArrowRight /></Button>
                    </div>
                </Group>
                <div className={classes.paginationGroup}>
                    <PoReport po_no={tableData[0]?.po_no} po_id={tableData[0]?.po_id} filter={filter} token={token} />
                    <Select
                        data={['10', '20', '30']}
                        defaultValue={'10'}
                        style={{ width: '70px' }}
                        onChange={(value) => setPagination({ ...pagination, limit: parseInt(value || '10') })}
                    />
                </div>
            </div>
            <Modal
                opened={filterModal}
                onClose={() => setFilterModal(false)}
                title={`Filter Options`}
                centered
                size={'lg'}
            >
                <div>
                    <Grid>
                        <Grid.Col span={6} style={{ gap: '20px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <Table withTableBorder withColumnBorders stickyHeader>
                                    <Table.Tbody>
                                        <Table.Tr>
                                            <Table.Th className={classes.tableHeadModal}>Style No</Table.Th>
                                            <Table.Td ><TextInput
                                                type='text'
                                                variant='unstyled'
                                                value={filter.style_no}
                                                className={classes.infoText}
                                                onChange={(e) => setFilter({ ...filter, style_no: e.target.value.toUpperCase() })}
                                            /></Table.Td>
                                        </Table.Tr>
                                        <Table.Tr>
                                            <Table.Th className={classes.tableHeadModal}>Po No</Table.Th>
                                            <Table.Td ><TextInput
                                                variant='unstyled'
                                                type='text'
                                                value={filter.po_no}
                                                onChange={(e) => setFilter({ ...filter, po_no: e.target.value.toUpperCase() })}
                                            /></Table.Td>
                                        </Table.Tr>
                                    </Table.Tbody>
                                </Table>
                            </div>
                        </Grid.Col>
                        <Grid.Col span={6} >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <Table withTableBorder withColumnBorders stickyHeader>
                                    <Table.Tbody>
                                        <Table.Tr>
                                            <Table.Th className={classes.tableHeadModal}>Supplier</Table.Th>
                                            <Table.Td ><TextInput
                                                type='text'
                                                variant='unstyled'
                                                value={filter.supplier_name}
                                                className={classes.infoText}
                                                onChange={(e) => setFilter({ ...filter, supplier_name: e.target.value.toUpperCase() })}
                                            /></Table.Td>
                                        </Table.Tr>
                                        <Table.Tr>
                                            <Table.Th className={classes.tableHeadModal}>Description</Table.Th>
                                            <Table.Td ><TextInput
                                                type='text'
                                                variant='unstyled'
                                                value={filter.description}
                                                className={classes.infoText}
                                                onChange={(e) => setFilter({ ...filter, description: e.target.value.toUpperCase() })}
                                            /></Table.Td>
                                        </Table.Tr>
                                    </Table.Tbody>
                                </Table>
                            </div>
                        </Grid.Col>
                    </Grid>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                    <Button style={{ backgroundColor: theme.colors.gray[8], width: '20vw' }} onClick={handleApplyFilter}>Apply</Button>
                </div>
            </Modal>
        </div>
    )
}


export default PoWorksheet;